import { isMissingDbObjectError } from "@/lib/supabase/missing-db-object-guard";

const BACKOFF_CURSOR_COLUMNS = [
  "consecutive_subscribe_failures",
  "last_subscribe_failure_at",
  "last_subscribe_attempt_at",
  "inhibit_until",
] as const;

type SupabaseLikeError = {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  hint?: unknown;
};

function getErrorText(error: SupabaseLikeError): string {
  return [error.message, error.details, error.hint]
    .filter((value): value is string => typeof value === "string")
    .join("\n")
    .toLowerCase();
}

/**
 * Detects the deploy-ordering window where app code references the optional
 * realtime subscribe-backoff columns before Supabase migrations/PostgREST schema
 * cache have caught up.
 *
 * Keep this intentionally narrow: only the additive realtime backoff columns
 * (WIT-625 subscribe counters/timestamps plus WIT-656 inhibit_until) are safe
 * to treat as optional rollout telemetry. Other schema errors should continue
 * to surface normally.
 */
export function isMissingBackoffColumnError(error: unknown): boolean {
  return isMissingDbObjectError(error, { columns: BACKOFF_CURSOR_COLUMNS });
}

const CDC_CONNECTION_BREAKER_COLUMNS = [
  "cdc_inhibit_until",
  "cdc_consecutive_failures",
  "cdc_last_failure_at",
  "cdc_inhibit_alerted_at",
] as const;

const CDC_CONNECTION_BREAKER_RPCS = [
  "trip_salesforce_cdc_connection_breaker",
  "reset_salesforce_cdc_connection_breaker",
] as const;

/**
 * Detects the deploy-ordering window where app code references the connection-
 * level CDC breaker columns/RPCs before the wit1150 migration + PostgREST schema
 * cache have caught up. Mirrors `isMissingBackoffColumnError`, but for the
 * `cdc_*` columns on `salesforce_connection` AND the two breaker RPCs
 * (`trip_salesforce_cdc_connection_breaker` / `reset_salesforce_cdc_connection_breaker`),
 * so both the trip and the reset can degrade to a warn-log no-op during deploy
 * ordering instead of throwing. Other schema errors continue to surface.
 */
export function isMissingCdcConnectionBreakerColumnError(error: unknown): boolean {
  return isMissingDbObjectError(error, {
    columns: CDC_CONNECTION_BREAKER_COLUMNS,
    rpcs: CDC_CONNECTION_BREAKER_RPCS,
  });
}

/**
 * `.single()` reports zero rows as an error. A missing sync_cursor row is normal
 * for a first realtime attempt or after reconnect cleanup, so treat only that
 * exact PostgREST no-row condition as healthy empty cursor state.
 */
export function isNoRowsCursorError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const supabaseError = error as SupabaseLikeError;
  const code = typeof supabaseError.code === "string" ? supabaseError.code : undefined;
  const text = getErrorText(supabaseError);
  return code === "PGRST116" || /(?:0 rows|no rows|contains 0 rows)/i.test(text);
}

const MIN_TRANSIENT_BACKOFF_MS = 30 * 60 * 1000;
const MAX_TRANSIENT_BACKOFF_MS = 24 * 60 * 60 * 1000;

export function calculateRealtimeSubscribeInhibitUntil(
  consecutiveFailures: number,
  now: Date = new Date(),
): Date {
  const safeFailures = Number.isFinite(consecutiveFailures)
    ? Math.max(1, Math.floor(consecutiveFailures))
    : 1;
  const multiplier = 2 ** Math.max(0, safeFailures - 1);
  const delayMs = Math.min(MAX_TRANSIENT_BACKOFF_MS, MIN_TRANSIENT_BACKOFF_MS * multiplier);
  return new Date(now.getTime() + delayMs);
}

export function shouldSkipRealtimeSubscribeForInhibitUntil(
  inhibitUntil: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!inhibitUntil) return false;
  const inhibitUntilMs = inhibitUntil instanceof Date ? inhibitUntil.getTime() : Date.parse(inhibitUntil);
  return Number.isFinite(inhibitUntilMs) && inhibitUntilMs > now.getTime();
}

// Connection/tenant-level CDC breaker backoff ladder: 4h -> 8h -> 16h -> 24h cap.
//
// SOURCE OF TRUTH: the stored `cdc_inhibit_until` deadline is computed in SQL by
// the `trip_salesforce_cdc_connection_breaker` RPC (see the wit1150 migration),
// which keeps these same 4h/24h constants. The constants and
// `calculateCdcConnectionInhibitUntil` below exist ONLY for the app-side
// calc/unit test and display; they must be kept in sync with that RPC.
export const MIN_CDC_CONNECTION_BACKOFF_MS = 4 * 60 * 60 * 1000;
export const MAX_CDC_CONNECTION_BACKOFF_MS = 24 * 60 * 60 * 1000;

/**
 * Mirror of the SQL trip RPC's backoff ladder for the connection-level CDC
 * breaker: `min(MAX, MIN * 2^(failures-1))`, clamped. Guards non-positive /
 * non-finite failure counts down to the MIN (4h) floor. The RPC is the source
 * of truth for the persisted deadline; this is the app-side equivalent.
 */
export function calculateCdcConnectionInhibitUntil(
  consecutiveFailures: number,
  now: Date = new Date(),
): Date {
  const safeFailures = Number.isFinite(consecutiveFailures)
    ? Math.max(1, Math.floor(consecutiveFailures))
    : 1;
  const multiplier = 2 ** Math.max(0, safeFailures - 1);
  const delayMs = Math.min(MAX_CDC_CONNECTION_BACKOFF_MS, MIN_CDC_CONNECTION_BACKOFF_MS * multiplier);
  return new Date(now.getTime() + delayMs);
}

/**
 * Connection-scoped skip check for the CDC breaker. Delegates to the same
 * future-timestamp comparison as `shouldSkipRealtimeSubscribeForInhibitUntil`,
 * so `cdc_inhibit_until == now` is treated as EXPIRED (a subscribe is attempted),
 * matching the `<= p_now` new-window rule in the trip RPC.
 */
export function shouldSkipCdcSubscribeForConnectionInhibit(
  inhibitUntil: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  return shouldSkipRealtimeSubscribeForInhibitUntil(inhibitUntil, now);
}

export type RealtimeCdcTransientErrorKind =
  | "resource_exhausted"
  | "topic_not_found"
  | "entity_upsert_error"
  | "stale_replay_cursor"
  | "grpc_internal"
  | "grpc_unavailable";

export const REALTIME_CDC_STALE_REPLAY_CURSOR_MESSAGE = "3 INVALID_ARGUMENT: Salesforce Pub/Sub replay cursor is stale; resetting to latest";

type RealtimeCdcClassifierOptions = {
  assumeRealtimeCdcContext?: boolean;
};

const REALTIME_CDC_CONTEXT_RE = /SalesforcePubSub(?:Error|EscapedError)|Salesforce Pub\/Sub|pubsub-stream|realtime_cdc|runRealtimeSyncForConfig|subscribeLatest|subscribeFromReplay|\/data\/[A-Za-z0-9_]+ChangeEvent/i;

function getRealtimeCdcErrorText(error: unknown): string {
  if (error instanceof Error) {
    const grpcDetails = (error as { grpcDetails?: unknown }).grpcDetails;
    const details = (error as { details?: unknown }).details;
    return [error.name, error.message, typeof grpcDetails === "string" ? grpcDetails : undefined, typeof details === "string" ? details : undefined, error.stack]
      .filter(Boolean)
      .join(" ");
  }

  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    const parts = [record.name, record.message, record.grpcDetails, record.details, record.stack]
      .filter((value): value is string => typeof value === "string" && value.length > 0);
    return parts.length > 0 ? parts.join(" ") : String(error);
  }

  return String(error);
}

function hasRealtimeCdcContext(text: string, options?: RealtimeCdcClassifierOptions): boolean {
  return options?.assumeRealtimeCdcContext === true || REALTIME_CDC_CONTEXT_RE.test(text);
}

function getRealtimeCdcGrpcCode(error: unknown): number | null {
  if (error && typeof error === "object") {
    const code = (error as { grpcCode?: unknown; code?: unknown }).grpcCode
      ?? (error as { grpcCode?: unknown; code?: unknown }).code;
    if (typeof code === "number" && Number.isFinite(code)) return code;
    if (typeof code === "string" && /^\d+$/.test(code)) return Number(code);
  }

  const text = getRealtimeCdcErrorText(error);
  const match = text.match(/\b(\d{1,2})\s+[A-Z_]+\b/);
  return match ? Number(match[1]) : null;
}

const STALE_REPLAY_CURSOR_TEXT_RES = [
  /\bReplay ID validation failed\b/i,
  /\bINVALID_REPLAY_ID\b/i,
  /\bREPLAY_ID_EXPIRED\b/i,
  // Match the package classifier's bounded wording fallback so defensive
  // app-level gRPC 3 handling stays narrow and does not swallow unrelated
  // INVALID_ARGUMENT programmer/configuration errors.
  /\breplay(?:\s*id|Id)?\b[^\n.]{0,80}\b(?:expired|invalid|not\s+found|out[-\s]+of[-\s]+range)\b/i,
] as const;

function hasStaleReplayCursorValidationText(text: string): boolean {
  return STALE_REPLAY_CURSOR_TEXT_RES.some((re) => re.test(text));
}

const REALTIME_CDC_TRANSIENT_KIND_PROP = "__handledRealtimeCdcTransientKind";
const REALTIME_CDC_PERSISTED_MESSAGE_PROP = "__handledRealtimeCdcPersistedMessage";

function isRealtimeCdcTransientErrorKind(value: unknown): value is RealtimeCdcTransientErrorKind {
  return value === "resource_exhausted"
    || value === "topic_not_found"
    || value === "entity_upsert_error"
    || value === "stale_replay_cursor"
    || value === "grpc_internal"
    || value === "grpc_unavailable";
}

function getAnnotatedRealtimeCdcError(error: unknown): {
  transientKind: RealtimeCdcTransientErrorKind;
  persistedMessage?: string;
} | null {
  if (!error || typeof error !== "object") return null;
  const record = error as Record<string, unknown>;
  const transientKind = record[REALTIME_CDC_TRANSIENT_KIND_PROP];
  if (!isRealtimeCdcTransientErrorKind(transientKind)) return null;
  const persistedMessage = typeof record[REALTIME_CDC_PERSISTED_MESSAGE_PROP] === "string"
    ? record[REALTIME_CDC_PERSISTED_MESSAGE_PROP]
    : undefined;
  return { transientKind, persistedMessage };
}

export function annotateRealtimeCdcError(
  error: unknown,
  details: { transientKind: RealtimeCdcTransientErrorKind | null; persistedMessage: string },
): void {
  if (!details.transientKind || !error || typeof error !== "object") return;

  try {
    Object.defineProperties(error, {
      [REALTIME_CDC_TRANSIENT_KIND_PROP]: {
        value: details.transientKind,
        configurable: true,
        writable: true,
      },
      [REALTIME_CDC_PERSISTED_MESSAGE_PROP]: {
        value: details.persistedMessage,
        configurable: true,
        writable: true,
      },
    });
  } catch {
    // Best-effort annotation only. Persistence paths still use local classification.
  }
}

export function classifyRealtimeCdcTransientError(
  error: unknown,
  options?: RealtimeCdcClassifierOptions,
): RealtimeCdcTransientErrorKind | null {
  const annotated = getAnnotatedRealtimeCdcError(error);
  if (annotated) return annotated.transientKind;

  const text = getRealtimeCdcErrorText(error);
  const grpcCode = getRealtimeCdcGrpcCode(error);
  const hasCdcContext = hasRealtimeCdcContext(text, options);

  if (/RESOURCE_EXHAUSTED|\b8\s+RESOURCE_EXHAUSTED/i.test(text)) {
    return "resource_exhausted";
  }

  if (/(?:\b5\s+)?NOT_FOUND/i.test(text) && /ChangeEvent/i.test(text) && /not found/i.test(text)) {
    return "topic_not_found";
  }

  if (/entity_upsert_error|record\(s\) failed during entity upsert/i.test(text)) {
    return "entity_upsert_error";
  }

  // Defensive only: the realtime runner should recover confirmed stale replay
  // cursors before they reach generic stream/Sentry handling. The replay
  // validation text is Salesforce-specific, so unlike broader 13/14 service
  // blips this can be recognized without broadening all route-level gRPC 3s.
  if (grpcCode === 3 && hasStaleReplayCursorValidationText(text)) {
    return "stale_replay_cursor";
  }

  if (hasCdcContext && (grpcCode === 13 || /(?:\b13\s+INTERNAL\b|\bINTERNAL:\s*Received RST_STREAM)/i.test(text))) {
    return "grpc_internal";
  }

  if (hasCdcContext && (grpcCode === 14 || /\b14\s+UNAVAILABLE\b/i.test(text))) {
    return "grpc_unavailable";
  }

  return null;
}

export const REALTIME_CDC_RESOURCE_EXHAUSTED_MESSAGE = "8 RESOURCE_EXHAUSTED: Salesforce Pub/Sub org-wide event delivery quota exhausted; CDC paused until quota window resets";

export function isRealtimeCdcServiceBlipKind(kind: RealtimeCdcTransientErrorKind | null): boolean {
  return kind === "grpc_internal" || kind === "grpc_unavailable" || kind === "resource_exhausted";
}

export function getRealtimeCdcServiceBlipPersistedMessage(kind: RealtimeCdcTransientErrorKind | null): string | null {
  if (kind === "grpc_unavailable") return "14 UNAVAILABLE: Salesforce Pub/Sub service temporarily unavailable";
  if (kind === "grpc_internal") return "13 INTERNAL: Salesforce Pub/Sub service returned a transient internal error";
  if (kind === "resource_exhausted") return REALTIME_CDC_RESOURCE_EXHAUSTED_MESSAGE;
  return null;
}

export function getRealtimeCdcTransientPersistedMessage(kind: RealtimeCdcTransientErrorKind | null): string | null {
  if (kind === "stale_replay_cursor") return REALTIME_CDC_STALE_REPLAY_CURSOR_MESSAGE;
  return getRealtimeCdcServiceBlipPersistedMessage(kind);
}

export { isRealtimeCdcServiceBlipMessage, isRealtimeCdcServiceBlipRun } from "./realtime-cdc-service-blips";

export function sanitizeRealtimeCdcErrorMessage(error: unknown): string {
  const rawMessage = error instanceof Error ? error.message : String(error);
  const collapsed = rawMessage.replace(/\s+/g, " ").trim();
  const grpcMatch = collapsed.match(/\b(13|14)\s+(INTERNAL|UNAVAILABLE)\b/i);
  const grpcCode = getRealtimeCdcGrpcCode(error);
  const canonicalCode = grpcMatch?.[1] ?? (grpcCode === 13 || grpcCode === 14 ? String(grpcCode) : null);
  const containsHtml = /<\s*!doctype\b|<\s*html\b|<\/[a-z][\s\S]*?>|<[a-z][\s\S]*?>/i.test(collapsed);

  if (canonicalCode && containsHtml) {
    const status = grpcMatch?.[2]?.toUpperCase() ?? (canonicalCode === "14" ? "UNAVAILABLE" : "INTERNAL");
    const summary = canonicalCode === "14"
      ? "Salesforce Pub/Sub service temporarily unavailable"
      : "Salesforce Pub/Sub service returned a transient internal error";
    return `${canonicalCode} ${status}: ${summary}`;
  }

  const withoutTags = collapsed.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (canonicalCode && !grpcMatch) {
    const status = canonicalCode === "14" ? "UNAVAILABLE" : "INTERNAL";
    const canonicalMessage = withoutTags || (canonicalCode === "14"
      ? "Salesforce Pub/Sub service temporarily unavailable"
      : "Salesforce Pub/Sub service returned a transient internal error");
    return `${canonicalCode} ${status}: ${canonicalMessage}`;
  }
  return withoutTags.length > 500 ? `${withoutTags.slice(0, 497)}...` : withoutTags;
}

export function describeRealtimeCdcError(
  error: unknown,
  options?: RealtimeCdcClassifierOptions,
): {
  transientKind: RealtimeCdcTransientErrorKind | null;
  isServiceBlip: boolean;
  persistedMessage: string;
  logLevel: "warn" | "error";
  logStatus: "warning" | "error";
  transientEventType: "sync_partial";
} {
  const annotated = getAnnotatedRealtimeCdcError(error);
  const transientKind = annotated?.transientKind ?? classifyRealtimeCdcTransientError(error, options);
  const isServiceBlip = isRealtimeCdcServiceBlipKind(transientKind);
  return {
    transientKind,
    isServiceBlip,
    persistedMessage: annotated?.persistedMessage
      ?? getRealtimeCdcTransientPersistedMessage(transientKind)
      ?? sanitizeRealtimeCdcErrorMessage(error),
    logLevel: transientKind ? "warn" : "error",
    logStatus: isServiceBlip ? "warning" : "error",
    transientEventType: "sync_partial",
  };
}
